import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Copy, Upload, Wand2 } from "lucide-react";

// entrance = { label, gen:{floorCount,aptsPerFloor,startNum,ground,groundCount}, floors:[{label, apartments:[str]}] }
function generateFloors(gen) {
  let n = parseInt(gen.startNum, 10) || 1;
  const floors = [];
  if (gen.ground) {
    const gc = parseInt(gen.groundCount, 10) || 0;
    const apts = [];
    for (let i = 0; i < gc; i++) { apts.push(String(n)); n++; }
    floors.push({ label: "Přízemí", apartments: apts });
  }
  const fc = Math.max(0, parseInt(gen.floorCount, 10) || 0);
  const per = parseInt(gen.aptsPerFloor, 10) || 0;
  for (let f = 1; f <= fc; f++) {
    const apts = [];
    for (let a = 0; a < per; a++) { apts.push(String(n)); n++; }
    floors.push({ label: String(f), apartments: apts });
  }
  return floors;
}

const emptyEntrance = (label) => ({
  label, gen: { floorCount: 3, aptsPerFloor: 4, startNum: 1, ground: false, groundCount: 2 },
  floors: generateFloors({ floorCount: 3, aptsPerFloor: 4, startNum: 1, ground: false, groundCount: 2 }),
});

export default function CreateSVJ() {
  const nav = useNavigate();
  const fileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [invite, setInvite] = useState(null);
  const [f, setF] = useState({ name: "", address: "", chairman_name: "", chairman_email: "", chairman_phone: "", operator_info: "" });
  const [entrances, setEntrances] = useState([emptyEntrance("1")]);

  const setEnt = (i, patch) => setEntrances((es) => es.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const total = entrances.reduce((s, e) => s + e.floors.reduce((x, fl) => x + fl.apartments.length, 0), 0);

  const regen = (i) => setEnt(i, { floors: generateFloors(entrances[i].gen) });

  const addFloor = (i) => setEnt(i, { floors: [...entrances[i].floors, { label: String(entrances[i].floors.length + 1), apartments: [] }] });
  const removeFloor = (i, fi) => setEnt(i, { floors: entrances[i].floors.filter((_, x) => x !== fi) });
  const setFloorLabel = (i, fi, label) => setEnt(i, { floors: entrances[i].floors.map((fl, x) => (x === fi ? { ...fl, label } : fl)) });
  const setApt = (i, fi, ai, val) => setEnt(i, { floors: entrances[i].floors.map((fl, x) => x === fi ? { ...fl, apartments: fl.apartments.map((a, y) => (y === ai ? val : a)) } : fl) });
  const addApt = (i, fi) => setEnt(i, { floors: entrances[i].floors.map((fl, x) => x === fi ? { ...fl, apartments: [...fl.apartments, ""] } : fl) });
  const removeApt = (i, fi, ai) => setEnt(i, { floors: entrances[i].floors.map((fl, x) => x === fi ? { ...fl, apartments: fl.apartments.filter((_, y) => y !== ai), owners: (fl.owners || []).filter((_, y) => y !== ai) } : fl) });

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/svjs/import-parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEntrances(data.entrances.map((en) => ({
        label: en.label, gen: { floorCount: 0, aptsPerFloor: 0, startNum: 1, ground: false, groundCount: 0 },
        floors: en.floors.map((fl) => ({ label: fl.label, apartments: fl.apartments.map(String), owners: (fl.owners || []).map(String) })),
      })));
      toast.success(`Importováno ${data.total} bytů z ${data.entrances.length} vchodů. Zkontrolujte a upravte.`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!f.name.trim() || !f.chairman_email.trim()) return toast.error("Vyplňte název SVJ a e-mail předsedy");
    // validate unique numbers within each entrance
    for (const e of entrances) {
      const nums = e.floors.flatMap((fl) => fl.apartments.map((a) => a.trim())).filter(Boolean);
      if (new Set(nums).size !== nums.length) return toast.error(`Vchod ${e.label}: čísla bytů musí být jedinečná`);
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        entrances: entrances.map((e) => ({
          label: e.label,
          floors: e.floors.map((fl) => {
            const pairs = fl.apartments.map((a, i) => [a.trim(), (fl.owners || [])[i] || ""]).filter(([a]) => a);
            return { label: fl.label, apartments: pairs.map((p) => p[0]), owners: pairs.map((p) => p[1]) };
          })
            .filter((fl) => fl.apartments.length > 0),
        })),
      };
      const { data } = await api.post("/svjs", payload);
      setInvite(data.invite_link);
      toast.success(`SVJ založeno · ${data.apartments} bytů vytvořeno`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (invite) return (
    <div className="max-w-lg space-y-5">
      <h1 className="text-2xl font-bold text-[#0A2540]">SVJ bylo založeno</h1>
      <p className="text-slate-600">Předsedovi byl vytvořen účet. Předejte mu tento odkaz pro nastavení hesla:</p>
      <div className="bg-slate-50 border rounded-lg p-3 flex items-center gap-2">
        <code className="text-sm flex-1 break-all" data-testid="invite-link">{invite}</code>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(invite); toast.success("Zkopírováno"); }}><Copy className="h-4 w-4" /></Button>
      </div>
      <Button onClick={() => nav("/admin")} style={{ background: "#0A2540" }}>Zpět na přehled</Button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2540]">Založení SVJ</h1>
        <div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf" className="hidden" onChange={onImport} data-testid="import-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-btn"><Upload className="h-4 w-4 mr-2" /> Importovat seznam jednotek</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 -mt-3">Podporované soubory: CSV, Excel nebo <b>PDF</b> se sloupci <b>vchod</b>, <b>patro</b>, <b>byt</b> (nebo jednotka „3416/12“) a volitelně <b>vlastník/jméno</b> – jméno se zobrazí obyvatelům u bytu. U PDF stačí i text „Vchod 1, 2. patro, byty 5–8“. Strukturu poté můžete ručně upravit.</p>

      <section className="bg-white rounded-xl border p-6 space-y-4">
        <h3 className="font-semibold text-[#0A2540]">Základní údaje</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Název SVJ *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="svj-name" /></Field>
          <Field label="Adresa"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="svj-address" /></Field>
          <Field label="Jméno předsedy"><Input value={f.chairman_name} onChange={(e) => setF({ ...f, chairman_name: e.target.value })} data-testid="chairman-name" /></Field>
          <Field label="E-mail předsedy (přihlašovací) *"><Input type="email" value={f.chairman_email} onChange={(e) => setF({ ...f, chairman_email: e.target.value })} data-testid="chairman-email" /></Field>
          <Field label="Telefon předsedy"><Input value={f.chairman_phone} onChange={(e) => setF({ ...f, chairman_phone: e.target.value })} data-testid="chairman-phone" /></Field>
        </div>
        <Field label="Informace o zpracování údajů / kontakt provozovatele">
          <Textarea value={f.operator_info} onChange={(e) => setF({ ...f, operator_info: e.target.value })} data-testid="operator-info" />
        </Field>
      </section>

      <section className="bg-white rounded-xl border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0A2540]">Struktura domu</h3>
          <Button size="sm" variant="outline" onClick={() => setEntrances([...entrances, emptyEntrance(String(entrances.length + 1))])} data-testid="add-entrance">
            <Plus className="h-4 w-4 mr-1" /> Přidat vchod
          </Button>
        </div>

        {entrances.map((e, i) => (
          <div key={i} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Field label="Označení vchodu" small><Input value={e.label} onChange={(ev) => setEnt(i, { label: ev.target.value })} className="w-24" data-testid={`entrance-label-${i}`} /></Field>
              {entrances.length > 1 && <Button size="sm" variant="ghost" className="text-red-500 mt-5" onClick={() => setEntrances(entrances.filter((_, x) => x !== i))}><Trash2 className="h-4 w-4" /></Button>}
            </div>

            {/* quick generator */}
            <div className="bg-slate-50 rounded-lg p-3 flex items-end gap-3 flex-wrap">
              <Field label="Počet pater" small><Input type="number" min="0" value={e.gen.floorCount} onChange={(ev) => setEnt(i, { gen: { ...e.gen, floorCount: ev.target.value } })} className="w-20" data-testid={`gen-floors-${i}`} /></Field>
              <Field label="Bytů/patro" small><Input type="number" min="0" value={e.gen.aptsPerFloor} onChange={(ev) => setEnt(i, { gen: { ...e.gen, aptsPerFloor: ev.target.value } })} className="w-20" data-testid={`gen-per-${i}`} /></Field>
              <Field label="Od čísla" small><Input type="number" value={e.gen.startNum} onChange={(ev) => setEnt(i, { gen: { ...e.gen, startNum: ev.target.value } })} className="w-20" data-testid={`gen-start-${i}`} /></Field>
              <label className="flex items-center gap-2 text-sm mb-1.5 border rounded px-2 py-1.5 bg-white">
                <Checkbox checked={e.gen.ground} onCheckedChange={(c) => setEnt(i, { gen: { ...e.gen, ground: !!c } })} data-testid={`gen-ground-${i}`} /> Přízemí
              </label>
              {e.gen.ground && <Field label="Bytů v přízemí" small><Input type="number" min="0" value={e.gen.groundCount} onChange={(ev) => setEnt(i, { gen: { ...e.gen, groundCount: ev.target.value } })} className="w-24" data-testid={`gen-ground-count-${i}`} /></Field>}
              <Button size="sm" variant="outline" onClick={() => regen(i)} data-testid={`gen-run-${i}`}><Wand2 className="h-4 w-4 mr-1" /> Vygenerovat patra</Button>
            </div>

            {/* editable floors */}
            <div className="space-y-2">
              {e.floors.map((fl, fi) => (
                <div key={fi} className="flex items-start gap-2 border rounded-lg p-2" data-testid={`floor-row-${i}-${fi}`}>
                  <div className="w-28 shrink-0">
                    <Label className="text-[10px] text-slate-400">Patro</Label>
                    <Input value={fl.label} onChange={(ev) => setFloorLabel(i, fi, ev.target.value)} className="h-8" data-testid={`floor-label-${i}-${fi}`} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px] text-slate-400">Byty ({fl.apartments.length})</Label>
                    <div className="flex gap-1.5 flex-wrap items-center">
                      {fl.apartments.map((a, ai) => (
                        <div key={ai} className="flex items-center">
                          <Input value={a} onChange={(ev) => setApt(i, fi, ai, ev.target.value)} className="h-8 w-16 text-center" data-testid={`apt-input-${i}-${fi}-${ai}`} />
                          <button onClick={() => removeApt(i, fi, ai)} className="text-slate-300 hover:text-red-500 -ml-5 z-10" title="Odebrat byt">×</button>
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="h-8 text-[#0A2540]" onClick={() => addApt(i, fi)} data-testid={`add-apt-${i}-${fi}`}><Plus className="h-3 w-3" /> byt</Button>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400 mt-4" onClick={() => removeFloor(i, fi)} data-testid={`remove-floor-${i}-${fi}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addFloor(i)} data-testid={`add-floor-${i}`}><Plus className="h-4 w-4 mr-1" /> Přidat patro</Button>
            </div>
            <div className="text-xs text-slate-500">Vchod {e.label}: {e.floors.reduce((s, fl) => s + fl.apartments.length, 0)} bytů</div>
          </div>
        ))}
      </section>

      <div className="flex items-center justify-between bg-white rounded-xl border p-5 sticky bottom-4 shadow">
        <div className="text-lg"><b>{total}</b> bytů celkem · <b>{entrances.length}</b> vchodů</div>
        <Button onClick={submit} disabled={saving} className="h-12 px-8" style={{ background: "#C5A059" }} data-testid="submit-svj">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Vytvořit SVJ
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children, small }) {
  return <div className="space-y-1.5"><Label className={small ? "text-xs" : ""}>{label}</Label>{children}</div>;
}
